import React, {useRef} from "react";
import {
  Card,
  CardActions,
  CardContent,
  Grid,
  ListItemText,
  Typography,
} from "@mui/material";
import Favourite from "./Favourite";

import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

import { i18n } from "../util/i18n";

import {useInViewport} from "ahooks";

const getPensum = (job) => {
    let min = job?.szas["sza_pensum.min"] || "";
    let max = job?.szas["sza_pensum.max"] || "";

    if (min == max) {
        return min? `${min} %` : "";
    } else {
        return `${min} ${min ? "-" : ""} ${max} %`;
    }
};

/**
 * Renders a single Job item, typically as part of a JobGrid.
 * This requires a "job" JSON object as a prop, as provided by the backend API.
 *
 *
 *
 * @param {job JSON object, as coming from the API} job
 * @param {subtitleAttribute} subtitleAttribute - the attribute to use as the subtitle (e.g. 80, to use job.attributes[80])
 * @param {favourites} favourites - "toggle" to show a favourite toggle button, "remove" to show the button as remove, "none" to hide the button
 * @returns JSX for the Job
 */
const JobGridItem = ({
  job,
  locationAttribute,
  berufsGruppeAttribute,
  klinikBereichAttribute,
  favourites = "toggle",
} = props) => {

  let cardElement = useRef(); // reference the Card React element below

  const [inViewport, ratio] = useInViewport(cardElement, {
      threshold: [0, 0.99, 1]
  });


  if (!job) {
    return <div>Invalid job, expected a job object</div>;
  }
  return (
    <Card
      variant="outlined"
      sx={{ minWidth: 100, height: "100%" }}
      className={"job-grid-item" + ((ratio >= 0.99) ? " inView" : " ")}
      key={"job-grid-item-" +job.id}
      ref={cardElement}
    >

      <a className="job" href={job.links.directlink} target="_blank">
          <CardContent sx={{ height: "33%" }}>
              <Typography variant="h5" component="div">
                <ListItemText
                    primary={job.title}
                />
              </Typography>
          </CardContent>
          <CardActions>
            <Grid container rowSpacing={1} flexDirection="row" justifyContent="space-between" alignItems="stretch">
              <Grid item className="meta" xs={12}>
                  <div className="icon" hidden={getPensum(job) == ""}>
                      <svg id="clock" xmlns="http://www.w3.org/2000/svg" width="19.422" height="19.422" viewBox="0 0 19.422 19.422">
                          <path  d="M8.214,3.607A.607.607,0,0,0,7,3.607v6.676a.607.607,0,0,0,.306.527l4.249,2.428a.607.607,0,0,0,.6-1.054L8.214,9.931Z" transform="translate(1.497 0.642)" fill="#040404"></path>
                          <path  d="M9.711,19.422A9.711,9.711,0,1,0,0,9.711,9.711,9.711,0,0,0,9.711,19.422Zm8.5-9.711a8.5,8.5,0,1,1-8.5-8.5A8.5,8.5,0,0,1,18.208,9.711Z" fill="#040404"></path>
                      </svg>
                  </div>
                  <Typography variant="caption">
                      {getPensum(job)}
                  </Typography>
              </Grid>
              <Grid className="meta" item xs={12}>
                  <div className="icon">
                      <svg id="geo-alt" xmlns="http://www.w3.org/2000/svg" width="14.668" height="19.557" viewBox="0 0 14.668 19.557">
                          <path d="M14.426,10.927a23.5,23.5,0,0,1-2.4,3.752,38.493,38.493,0,0,1-2.7,3.141,38.48,38.48,0,0,1-2.7-3.141,23.5,23.5,0,0,1-2.4-3.752A8.457,8.457,0,0,1,3.222,7.334a6.111,6.111,0,0,1,12.223,0,8.474,8.474,0,0,1-1.019,3.594ZM9.334,19.557s7.334-6.95,7.334-12.223A7.334,7.334,0,0,0,2,7.334C2,12.607,9.334,19.557,9.334,19.557Z" transform="translate(-2)" fill="#040404"></path>
                          <path d="M8.667,9.111a2.445,2.445,0,1,1,2.445-2.445A2.445,2.445,0,0,1,8.667,9.111Zm0,1.222A3.667,3.667,0,1,0,5,6.667,3.667,3.667,0,0,0,8.667,10.334Z" transform="translate(-1.333 0.667)" fill="#040404"></path>
                      </svg>
                  </div>
                  <Typography variant="caption">
                      { locationAttribute ? job.attributes[locationAttribute]?.join(", ") : null }
                  </Typography>
              </Grid>
              <Grid className="meta" item xs={12}>
                  <div className="icon">
                      <svg xmlns="http://www.w3.org/2000/svg" width="18.641" height="15.146" viewBox="0 0 18.641 15.146">
                          <path id="briefcase" d="M7.573,1A1.748,1.748,0,0,0,5.825,2.748V3.33H1.748A1.748,1.748,0,0,0,0,5.078V14.4a1.748,1.748,0,0,0,1.748,1.748H16.894A1.748,1.748,0,0,0,18.641,14.4V5.078A1.748,1.748,0,0,0,16.894,3.33H12.816V2.748A1.748,1.748,0,0,0,11.068,1Zm0,1.165h3.5a.583.583,0,0,1,.583.583V3.33H6.99V2.748A.583.583,0,0,1,7.573,2.165Zm2.2,8.055,7.706-2.054V14.4a.583.583,0,0,1-.583.583H1.748a.583.583,0,0,1-.583-.583V8.165L8.871,10.22a1.748,1.748,0,0,0,.9,0ZM1.748,4.5H16.894a.583.583,0,0,1,.583.583V6.961L9.471,9.095a.583.583,0,0,1-.3,0L1.165,6.961V5.078A.583.583,0,0,1,1.748,4.5Z" transform="translate(0 -1)" fill="#040404"></path>
                      </svg>
                  </div>
                  <Typography variant="caption">
                      { berufsGruppeAttribute ? job.attributes[berufsGruppeAttribute]?.join(", ") : null }
                  </Typography>
              </Grid>
                <Grid className="meta" item xs={12}>
                    <div className="icon">
                        <svg id="hospital" xmlns="http://www.w3.org/2000/svg" width="16.298" height="16.298" viewBox="0 0 16.298 16.298">
                            <path d="M8.621,5.034v1.12l.971-.56.509.883-.971.559.971.56-.509.882-.971-.56v1.12H7.6V7.919l-.971.56L6.122,7.6l.971-.56-.971-.56.509-.882.971.56V5.034Zm4.838,4.04a.255.255,0,0,0-.255.255v.509a.255.255,0,0,0,.255.255h.509a.255.255,0,0,0,.255-.255V9.328a.255.255,0,0,0-.255-.255ZM13.2,11.366a.255.255,0,0,1,.255-.255h.509a.255.255,0,0,1,.255.255v.509a.255.255,0,0,1-.255.255h-.509a.255.255,0,0,1-.255-.255Zm.255,1.783a.255.255,0,0,0-.255.255v.509a.255.255,0,0,0,.255.255h.509a.255.255,0,0,0,.255-.255V13.4a.255.255,0,0,0-.255-.255ZM2.255,9.074A.255.255,0,0,0,2,9.328v.509a.255.255,0,0,0,.255.255h.509a.255.255,0,0,0,.255-.255V9.328a.255.255,0,0,0-.255-.255Zm0,2.037A.255.255,0,0,0,2,11.366v.509a.255.255,0,0,0,.255.255h.509a.255.255,0,0,0,.255-.255v-.509a.255.255,0,0,0-.255-.255ZM2,13.4a.255.255,0,0,1,.255-.255h.509a.255.255,0,0,1,.255.255v.509a.255.255,0,0,1-.255.255H2.255A.255.255,0,0,1,2,13.912Z" transform="translate(0.037 0.094)" fill="#040404"></path>
                            <path d="M5.093,1.019A1.019,1.019,0,0,1,6.112,0h4.074A1.019,1.019,0,0,1,11.2,1.019V2.037a1.019,1.019,0,0,1,1.019,1.019V7.13h3.056A1.019,1.019,0,0,1,16.3,8.149v7.13A1.019,1.019,0,0,1,15.279,16.3H1.019A1.019,1.019,0,0,1,0,15.279V8.149A1.019,1.019,0,0,1,1.019,7.13H4.074V3.056A1.019,1.019,0,0,1,5.093,2.037ZM7.13,15.279H9.168V12.223H7.13Zm3.056,0H11.2V3.056H5.093V15.279H6.112V12.223A1.019,1.019,0,0,1,7.13,11.2H9.168a1.019,1.019,0,0,1,1.019,1.019Zm0-14.261H6.112V2.037h4.074Zm2.037,7.13v7.13h3.056V8.149Zm-8.149,7.13V8.149H1.019v7.13Z" fill="#040404"></path>
                        </svg>
                    </div>
                    <Typography variant="caption">
                        { klinikBereichAttribute ? job.attributes[klinikBereichAttribute]?.join(", ") : null }
                    </Typography>
                </Grid>
            </Grid>
          </CardActions>
        </a>
    </Card>
  );
};

export default JobGridItem;
